import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { Route, Switch } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import DashboardLayout from "./components/DashboardLayout";
import { ThemeProvider } from "./contexts/ThemeContext";
import { lazy, Suspense } from "react";
import { AffiliateSelectionProvider } from "./components/AffiliateSelector";

const Home = lazy(() => import("./pages/Home"));
const ROI = lazy(() => import("./pages/ROI"));
const Trafego = lazy(() => import("./pages/Trafego"));
const Criativos = lazy(() => import("./pages/Criativos"));
const ContasDeAnuncios = lazy(() => import("./pages/ContasDeAnuncios"));
const HookRate = lazy(() => import("./pages/HookRate"));
const InfluencerPage = lazy(() => import("./pages/Influencer"));
const UsuariosPage = lazy(() => import("./pages/Usuarios"));
const ConfigPage = lazy(() => import("./pages/Config"));
const VendasPage = lazy(() => import("./pages/Vendas"));
const BlingDashboard = lazy(() => import("./pages/BlingDashboard"));
const BlingLille = lazy(() => import("./pages/BlingLille"));
const FirstPurchase = lazy(() => import("./pages/FirstPurchase"));
const DiscountFreight = lazy(() => import("./pages/DiscountFreight"));
const MetasPage = lazy(() => import("./pages/Metas"));
const GastosPessoaProduto = lazy(() => import("./pages/GastosPessoaProduto"));
const AdPreview = lazy(() => import("./pages/AdPreview"));
const RoteirosPage = lazy(() => import("./pages/Roteiros"));
const ElasticidadePage = lazy(() => import("./pages/Elasticidade"));
const TasksPage = lazy(() => import("./pages/Tasks"));
const ScriptGenerator = lazy(() => import("./pages/ScriptGenerator"));
const GeradorRoteirosReferencias = lazy(() => import("./pages/GeradorRoteirosReferencias"));
const PromptManager = lazy(() => import("./pages/PromptManager"));
const ScriptLibrary = lazy(() => import("./pages/ScriptLibrary"));
const Afiliados = lazy(() => import("./pages/Afiliados"));
const PharmacyImport = lazy(() => import("./pages/PharmacyImport"));
const TrackingPage = lazy(() => import("./pages/Tracking"));
const LTV = lazy(() => import("./pages/LTV"));
const CohortAnalysis = lazy(() => import("./pages/CohortAnalysis"));
const PartnerDashboard = lazy(() => import("./pages/PartnerDashboard"));
const PartnerOrders = lazy(() => import("./pages/PartnerOrders"));
const PartnerCoupons = lazy(() => import("./pages/PartnerCoupons"));
const PartnerMaterials = lazy(() => import("./pages/PartnerMaterials"));
const PartnerCommissions = lazy(() => import("./pages/PartnerCommissions"));
const PartnerDeliverables = lazy(() => import("./pages/PartnerDeliverables"));
const PartnerSupport = lazy(() => import("./pages/PartnerSupport"));
const WhatsAppDashboard = lazy(() => import("./pages/WhatsAppDashboard"));
const BlackFridayLeads = lazy(() => import("./pages/BlackFridayLeads"));
const MercadoLivre = lazy(() => import("./pages/MercadoLivre"));
const ManagerDashboard = lazy(() => import("./pages/ManagerDashboard"));
const SubPortfolioDashboard = lazy(() => import("./pages/SubPortfolioDashboard"));
const ConversionRate = lazy(() => import("./pages/ConversionRate"));
const LoginPage = lazy(() => import("./pages/Login"));
const CadastroPage = lazy(() => import("./pages/Cadastro"));
const SharedScript = lazy(() => import("./pages/SharedScript"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const MinhaIA = lazy(() => import("./pages/MinhaIA"));

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="h-6 w-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function Router() {
  return (
    <DashboardLayout>
      <Suspense fallback={<PageLoader />}>
        <Switch>
          <Route path="/" component={Home} />
          <Route path="/diretoria/roi" component={ROI} />
          <Route path="/diretoria/trafego" component={Trafego} />
          <Route path="/diretoria/vendas" component={VendasPage} />
          <Route path="/diretoria/bling" component={BlingDashboard} />
          <Route path="/diretoria/bling-lille" component={BlingLille} />
          <Route path="/diretoria/primeira-compra" component={FirstPurchase} />
          <Route path="/diretoria/desconto-frete" component={DiscountFreight} />
          <Route path="/diretoria/gastos" component={GastosPessoaProduto} />
          <Route path="/diretoria/mercado-livre" component={MercadoLivre} />
          <Route path="/admin/metas" component={MetasPage} />
          <Route path="/gestao/criativos" component={Criativos} />
          <Route path="/gestao/contas-de-anuncios" component={ContasDeAnuncios} />
          <Route path="/gestao/hook-rate" component={HookRate} />
          <Route path="/influencer" component={InfluencerPage} />
          <Route path="/gestao/roteiros" component={RoteirosPage} />
          <Route path="/gestao/elasticidade" component={ElasticidadePage} />
          <Route path="/gestao/tasks" component={TasksPage} />
          <Route path="/gestao/gerador-roteiros" component={ScriptGenerator} />
          <Route path="/gestao/gerador-roteiros-referencias" component={GeradorRoteirosReferencias} />
          <Route path="/gestao/biblioteca-roteiros" component={ScriptLibrary} />
          <Route path="/gestao/afiliados" component={Afiliados} />
          <Route path="/gestao/importar-pharmacy" component={PharmacyImport} />
          <Route path="/gestao/gestor-dashboard" component={ManagerDashboard} />
          <Route path="/gestao/sub-portfolios" component={SubPortfolioDashboard} />
          <Route path="/diretoria/tracking" component={TrackingPage} />
          <Route path="/diretoria/ltv" component={LTV} />
          <Route path="/diretoria/cohort" component={CohortAnalysis} />
          <Route path="/diretoria/conversao" component={ConversionRate} />
          <Route path="/parceiro/:rest*">
            <AffiliateSelectionProvider>
              <Switch>
                <Route path="/parceiro/dashboard" component={PartnerDashboard} />
                <Route path="/parceiro/vendas" component={PartnerOrders} />
                <Route path="/parceiro/comissoes" component={PartnerCommissions} />
                <Route path="/parceiro/cupons" component={PartnerCoupons} />
                <Route path="/parceiro/materiais" component={PartnerMaterials} />
                <Route path="/parceiro/entregas" component={PartnerDeliverables} />
                <Route path="/parceiro/suporte" component={PartnerSupport} />
              </Switch>
            </AffiliateSelectionProvider>
          </Route>
          <Route path="/comercial/whatsapp" component={WhatsAppDashboard} />
          <Route path="/comercial/black-friday" component={BlackFridayLeads} />
          <Route path="/minha-ia" component={MinhaIA} />
          <Route path="/admin/usuarios" component={UsuariosPage} />
          <Route path="/admin/config" component={ConfigPage} />
          <Route path="/admin/prompts" component={PromptManager} />
          <Route path="/404" component={NotFound} />
          <Route component={NotFound} />
        </Switch>
      </Suspense>
    </DashboardLayout>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="dark">
        <TooltipProvider>
          <Toaster />
          <Switch>
            {/* Auth pages - outside DashboardLayout */}
            <Route path="/login">
              <Suspense fallback={<PageLoader />}>
                <LoginPage />
              </Suspense>
            </Route>
            <Route path="/cadastro">
              <Suspense fallback={<PageLoader />}>
                <CadastroPage />
              </Suspense>
            </Route>
            {/* Public preview page - outside DashboardLayout (no auth required) */}
            <Route path="/preview/:adId">
              <Suspense fallback={<PageLoader />}>
                <AdPreview />
              </Suspense>
            </Route>
            {/* Public shared script page */}
            <Route path="/roteiro/:token">
              <Suspense fallback={<PageLoader />}>
                <SharedScript />
              </Suspense>
            </Route>
            {/* Password recovery pages */}
            <Route path="/esqueci-senha">
              <Suspense fallback={<PageLoader />}>
                <ForgotPassword />
              </Suspense>
            </Route>
            <Route path="/redefinir-senha">
              <Suspense fallback={<PageLoader />}>
                <ResetPassword />
              </Suspense>
            </Route>
            {/* All other routes go through DashboardLayout */}
            <Route>
              <Router />
            </Route>
          </Switch>
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
